import { parse, parseISO, isValid, format } from 'date-fns'
import { enGB } from 'date-fns/locale'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import conf from '@/config'

const DISPLAY_FORMAT = 'dd.MM.yyyy'
const QUARTER_START_MONTHS = [0, 3, 6, 9]
const HOME_TZ = conf.VITE_TIMEZONE || 'Europe/Moscow'
const HOME_MONTHS =
  'Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec'

/** Уже готовая строка отображения в духе AS-2000: Q# / Mon YYYY / dd Mon YYYY */
const HOME_DISPLAY_RE =
  /^(?:Q[1-4]\s+\d{4}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}|\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4})$/i

/**
 * Допустимые символы для ввода даты: цифры, точка, пробел, дефис (для диапазона).
 * Остальное удаляется.
 */
export function sanitizeDateString(str) {
  if (str == null || typeof str !== 'string') return ''
  return str.replace(/[^\d.\s-]/g, '')
}

/**
 * Нормализует ввод вида "11112026" или "111126" в "11.11.2026" или "11.11.26".
 * - 8+ цифр: ddmmyyyy (лишние после года отбрасываются) -> dd.mm.yyyy
 * - 6 цифр: ddmmyy -> dd.mm.yy (двузначный год)
 * - Уже с точками не трогаем (только trim), если цифр не больше 8.
 */
export function normalizeToDotted(str) {
  const s = String(str).trim()
  const digitsOnly = s.replace(/\D/g, '')
  // Год — ровно 4 цифры; всё длиннее 8 цифр обрезаем (ввод вроде 04.08.2028000)
  if (digitsOnly.length >= 8) {
    return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2, 4)}.${digitsOnly.slice(4, 8)}`
  }
  if (digitsOnly.length === 6) {
    return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2, 4)}.${digitsOnly.slice(4, 6)}`
  }
  return s
}

/**
 * Автоматически подставляет точки при ручном наборе:
 * - "1" -> "1"
 * - "12" -> "12"
 * - "123" -> "12.3"
 * - "1234" -> "12.34"
 * - "12345" -> "12.34.5"
 * Год ограничен 4 цифрами (всего не больше 8 цифр: ддммгггг).
 * Не применяется для диапазонов (если есть '-') и кварталов (Q1 2026).
 */
export function autoInsertDots(value) {
  if (value == null) return ''
  const s = String(value)
  if (s.includes('-')) return s
  const trimmed = s.trim()
  if (/^q\d/i.test(trimmed)) return s
  // Уже с точками не пересобираем: «1.10.26» иначе становится «11.02.6».
  if (s.includes('.')) return capDottedYear(s)
  // dd.MM.yyyy — не больше 8 цифр, иначе год «растёт» без ограничений
  const digits = s.replace(/\D/g, '').slice(0, 8)
  if (!digits) return trimmed
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`
}

/** Год в «дд.мм.гггг» — не длиннее 4 цифр. */
function capDottedYear(value) {
  const match = /^(.*?\..*?\.)(\d+)(.*)$/.exec(value)
  if (!match) return value
  return `${match[1]}${match[2].slice(0, 4)}${match[3]}`
}

/**
 * «01.10.26» / «1.10.2026» → локальная полночь этого календарного дня.
 * Двузначный год: 00–68 → 2000–2068, 69–99 → 1969–1999.
 * Не полдень UTC: иначе 1 октября в UTC+5 (ещё 30 сентября UTC) читается как Q3.
 */
function parseDottedCalendarDate(normalized) {
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{1,4})$/.exec(normalized)
  if (!match) return null
  const day = Number(match[1])
  const month = Number(match[2])
  let year = Number(match[3])
  if (match[3].length <= 2) {
    year += year <= 68 ? 2000 : 1900
  }
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1) return null
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  return date
}

/** Строка похожа на ISO дату (с бэка приходят такие после сохранения). */
function isIsoLike(str) {
  const s = String(str).trim()
  return s.length >= 10 && s[4] === '-' && s[7] === '-'
}

export function parseQuarterInput(value) {
  if (!value || typeof value !== 'string') return null
  const s = value.trim()
  if (s[0] !== 'Q' && s[0] !== 'q') return null

  const rest = s.slice(1).replace('/', ' ').replace('-', ' ').trim()
  const parts = rest.split(/\s+/)
  if (parts.length !== 2) return null

  const quarter = parseInt(parts[0], 10)
  const year = parseInt(parts[1], 10)
  if (quarter < 1 || quarter > 4 || year < 1900 || year > 2100) return null

  const month = QUARTER_START_MONTHS[quarter - 1]
  // Полдень UTC 1-го числа квартала — тот же маркер, что пишет бэкенд.
  // Обычная дата 01.10.2026 (локальная полночь) этим не является и не станет Q4.
  const d = new Date(Date.UTC(year, month, 1, 12, 0, 0))
  return isValid(d) ? d : null
}

export function formatQuarterFromDate(date) {
  if (!(date instanceof Date) || !isValid(date)) return null

  // Маркер квартала с бэкенда: ровно полдень UTC 1 января, апреля, июля или октября.
  if (
    date.getUTCHours() !== 12 ||
    date.getUTCMinutes() !== 0 ||
    date.getUTCSeconds() !== 0 ||
    date.getUTCDate() !== 1
  ) {
    return null
  }
  const month = date.getUTCMonth()
  const quarterIndex = QUARTER_START_MONTHS.indexOf(month)
  if (quarterIndex === -1) return null
  // Локальный день должен быть тем же 1-м числом. Иначе 01.10 в UTC+5
  // (30.09 19:00 UTC, это ещё Q3 по UTC) нельзя подписать кварталом.
  if (date.getFullYear() !== date.getUTCFullYear() || date.getMonth() !== month || date.getDate() !== 1) {
    return null
  }
  return `Q${quarterIndex + 1} ${date.getUTCFullYear()}`
}

/**
 * Парсит одну строку даты (для одного поля или одной части диапазона).
 * Принимает ISO с бэка ("2026-02-11", "2026-02-11T00:00:00.000Z"), "11.11.2026", "11112026", "11.11.26".
 * Не бросает исключений — при любой ошибке возвращает null (избегаем RangeError: Invalid time value).
 * @returns {Date|null}
 */
export function parseDateInput(value) {
  try {
    if (value == null || typeof value !== 'string') return null
    const s = String(value).trim()
    if (!s) return null

    const fromQuarter = parseQuarterInput(s)
    if (fromQuarter) return fromQuarter

    // Сначала пробуем ISO (ответ API после сохранения), иначе поля дат в карточках очищаются
    if (isIsoLike(s)) {
      try {
        const d = parseISO(s)
        if (d && isValid(d) && !Number.isNaN(d.getTime())) return d
      } catch {
        // fall through
      }
    }

    const normalized = normalizeToDotted(s)
    if (!normalized) return null

    // Свой разбор дд.мм.гг: date-fns yyyy читает «26» как год 0026, а полночь 1 октября
    // в поясе восточнее UTC — это ещё предыдущие сутки UTC и чужой квартал.
    const dotted = parseDottedCalendarDate(normalized)
    if (dotted) return dotted

    const yearPart = normalized.split('.').pop() ?? ''
    const formats = /^\d{1,2}$/.test(yearPart)
      ? ['dd.MM.yy', 'd.M.yy', 'dd.M.yy', 'd.MM.yy']
      : [DISPLAY_FORMAT, 'd.M.yyyy', 'dd.M.yyyy', 'd.MM.yyyy']
    for (const fmt of formats) {
      try {
        const referenceDate = new Date()
        referenceDate.setHours(0, 0, 0, 0)
        const d = parse(normalized, fmt, referenceDate)
        if (d && isValid(d) && !Number.isNaN(d.getTime())) return d
      } catch {
        continue
      }
    }
  } catch {
    // любая ошибка (в т.ч. RangeError) — не пробрасываем
  }
  return null
}

/**
 * Календарный день так, как он написан в поле даты, без перевода в UTC.
 * Date.toJSON() для полуночи UTC+5 даёт ещё предыдущие сутки, и письмо
 * показывает 12.10 вместо выбранных 13.10.
 *
 * @param {Date|string|null|undefined} value
 * @returns {string|null}
 */
export function expiryDatePayload(value) {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    if (!isValid(value)) return null
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${value.getFullYear()}-${month}-${day}`
  }
  const raw = String(value).trim()
  if (!raw) return null
  const bare = raw.match(/^(\d{4}-\d{2}-\d{2})$/)
  if (bare) return bare[1]
  const parsed = parseDateInput(raw)
  return parsed ? expiryDatePayload(parsed) : null
}

/**
 * Форматирует дату к виду "dd.MM.yyyy" для отображения в поле.
 * @param {Date|null} date
 * @returns {string}
 */
export function formatDateForInput(date, options = {}) {
  if (date == null) return ''
  if (!(date instanceof Date) || !isValid(date)) return ''
  try {
    if (options.allowQuarterFormat !== false) {
      const asQuarter = formatQuarterFromDate(date)
      if (asQuarter) return asQuarter
    }
    return format(date, DISPLAY_FORMAT)
  } catch {
    return ''
  }
}

export function expandDateSearchVariants(value) {
  const search = String(value ?? '').trim()
  if (!search) return value

  const date = parseDateInput(search)
  if (!date) return value

  const variants = [search, format(date, 'yyyy-MM-dd')]
  return [...new Set(variants)].join('|')
}

/** ISO-строка или Date с API → «dd.MM.yyyy» для readonly-полей. */
export function formatDateValueForDisplay(value) {
  if (value == null || value === '') return ''
  if (value instanceof Date) {
    return formatDateForInput(value)
  }
  const parsed = parseDateInput(String(value))
  return parsed ? formatDateForInput(parsed) : ''
}

function normalizeHomeDisplayString(s) {
  const t = s.trim()
  const q = t.match(/^Q([1-4])\s+(\d{4})$/i)
  if (q) return `Q${q[1]} ${q[2]}`
  const mon = t.match(new RegExp(`^(${HOME_MONTHS})\\s+(\\d{4})$`, 'i'))
  if (mon) {
    return `${mon[1].slice(0, 1).toUpperCase()}${mon[1].slice(1, 3).toLowerCase()} ${mon[2]}`
  }
  const day = t.match(new RegExp(`^(\\d{1,2})\\s+(${HOME_MONTHS})\\s+(\\d{4})$`, 'i'))
  if (day) {
    const dd = String(parseInt(day[1], 10)).padStart(2, '0')
    const m = `${day[2].slice(0, 1).toUpperCase()}${day[2].slice(1, 3).toLowerCase()}`
    return `${dd} ${m} ${day[3]}`
  }
  return t
}

/** Как commons.dateLocal: строка API → Date в Europe/Moscow. */
function toHomeZonedDate(value) {
  if (value instanceof Date) {
    return isValid(value) ? value : null
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value)
    return isValid(d) ? d : null
  }
  const s = String(value ?? '').trim()
  if (!s) return null

  const fromQuarter = parseQuarterInput(s)
  if (fromQuarter) return fromQuarter

  // «Aug 2026» → 1-е число месяца (месячная точность макета)
  const mon = s.match(new RegExp(`^(${HOME_MONTHS})\\s+(\\d{4})$`, 'i'))
  if (mon) {
    const monthIdx = HOME_MONTHS.split('|').findIndex(
      m => m.toLowerCase() === mon[1].slice(0, 3).toLowerCase()
    )
    if (monthIdx >= 0) {
      const d = fromZonedTime(
        `${mon[2]}-${String(monthIdx + 1).padStart(2, '0')}-01`,
        HOME_TZ
      )
      if (d && isValid(d)) return d
    }
  }

  // Date-only «2026-01-01» — локаль приложения (как dateLocal / quarter sentinel)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    try {
      const zoned = fromZonedTime(s, HOME_TZ)
      if (zoned && isValid(zoned) && !Number.isNaN(zoned.getTime())) return zoned
    } catch {
      /* fall through */
    }
  }

  // ISO с зоной/временем — абсолютный момент (не через fromZonedTime: ломает Z)
  if (isIsoLike(s) || /^\d{4}-\d{2}-\d{2}T/.test(s)) {
    try {
      const iso = parseISO(s)
      if (iso && isValid(iso) && !Number.isNaN(iso.getTime())) return iso
    } catch {
      /* fall through */
    }
  }

  try {
    const zoned = fromZonedTime(s, HOME_TZ)
    if (zoned && isValid(zoned) && !Number.isNaN(zoned.getTime())) {
      return zoned
    }
  } catch {
    /* fall through */
  }

  return parseDateInput(s)
}

/**
 * Отображение дат на стартовой / Pipeline / Journey как в макете AS-2000:
 * - квартал → «Q4 2026»
 * - месяц (1-е число, не квартальный sentinel) → «Oct 2026»
 * - полный день → «08 Sep 2025»
 * - готовые строки макета — как есть
 *
 * Не меняет commons.dateLocal / formatDateForInput — только display на home.
 * @param {string|Date|number|null|undefined} value
 * @param {{ empty?: string }} [options]
 * @returns {string}
 */
export function formatHomeDateDisplay(value, options = {}) {
  const empty = options.empty ?? ''
  if (value == null || value === '') return empty

  if (typeof value === 'string') {
    const s = value.trim()
    if (!s || s === '—' || s === '-') return empty || '—'
    if (HOME_DISPLAY_RE.test(s)) return normalizeHomeDisplayString(s)
    // Сырой Date#toString / GMT — не показываем, пробуем распарсить
    if (/^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),/i.test(s)) {
      const parsedGmt = new Date(s)
      if (isValid(parsedGmt)) {
        return formatHomeDateDisplay(parsedGmt, options)
      }
      return empty || '—'
    }
  }

  const date = toHomeZonedDate(value)
  if (!date || !isValid(date)) return empty

  try {
    const asQuarter = formatQuarterFromDate(date)
    if (asQuarter) return asQuarter

    // Месячная точность: 1-е число в TZ приложения (Feb/Mar/May/… → «Aug 2026»)
    const dayInTz = formatInTimeZone(date, HOME_TZ, 'd')
    if (dayInTz === '1') {
      return formatInTimeZone(date, HOME_TZ, 'MMM yyyy', { locale: enGB })
    }

    return formatInTimeZone(date, HOME_TZ, 'dd MMM yyyy', { locale: enGB })
  } catch {
    return empty
  }
}
